import React, { useState, useEffect, useMemo } from 'react'
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import axiosInstance from '../../api/axiosInstance'
import { usePermissions } from '../../hooks/usePermissions'
import { transportadora as transportadoraOptions } from '../../assets/data/arrays'
import {
  areDocumentLinksDuplicate,
  DUPLICATE_DOCUMENT_LINKS_MESSAGE
} from '../../utils/updateDocumentLinksUtils'

const isMercadoLibreStore = (storeName: string): boolean =>
  storeName.toLowerCase().includes('mercado libre')

const isEnviaTransportadora = (value: string): boolean =>
  value.trim().toLowerCase().replace(/í/g, 'i') === 'envia'

const hasUnidadesEmpaqueDefinidas = (value: unknown): boolean => {
  if (value == null || value === '') return false
  if (typeof value === 'number') return Number.isFinite(value) && value >= 1
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return /^\d+$/.test(trimmed) && Number(trimmed) >= 1
  }
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value as object).length > 0
  return false
}

const isUnidadesEmpaqueValidas = (value: string): boolean => {
  if (!/^\d+$/.test(value)) return false
  const unidades = Number(value)
  return Number.isInteger(unidades) && unidades >= 1
}

interface UpdateDocumentsModalProps {
  isOpen: boolean
  onClose: () => void
  orderId: number
  currentDocumento1?: string
  currentDocumento2?: string
  currentTransportadora?: string
  currentGuia?: string
  currentUnidadesEmpaque?: unknown
  currentExtraDetails?: Record<string, unknown>
  storeName?: string
  onSuccess: () => void
  [key: string]: unknown
}

const UpdateDocumentsModal: React.FC<UpdateDocumentsModalProps> = ({
  isOpen,
  onClose,
  orderId,
  currentDocumento1 = '',
  currentDocumento2 = '',
  currentTransportadora = '',
  currentGuia = '',
  currentUnidadesEmpaque,
  currentExtraDetails,
  storeName = '',
  onSuccess
}) => {
  const [documento1, setDocumento1] = useState(currentDocumento1)
  const [documento2, setDocumento2] = useState(currentDocumento2)
  const [transportadora, setTransportadora] = useState(currentTransportadora)
  const [guia, setGuia] = useState(currentGuia)
  const [unidadesEmpaque, setUnidadesEmpaque] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { hasPermission } = usePermissions()
  const isHomecenter = (storeName || '').toLowerCase().includes('homecenter')
  const pideUnidadesEmpaque =
    isMercadoLibreStore(storeName) &&
    isEnviaTransportadora(currentTransportadora) &&
    !hasUnidadesEmpaqueDefinidas(currentUnidadesEmpaque)
  const unidadesValidas = isUnidadesEmpaqueValidas(unidadesEmpaque)

  const documentLinksAreDuplicate = useMemo(
    () => areDocumentLinksDuplicate(documento1, documento2),
    [documento1, documento2]
  )

  const hasMinimumUpdateFields = useMemo(() => {
    const hasDocuments = Boolean(documento1.trim() || documento2.trim())
    const hasHomecenterFields =
      isHomecenter && Boolean(transportadora.trim() || guia.trim())
    return hasDocuments || hasHomecenterFields
  }, [documento1, documento2, isHomecenter, transportadora, guia])

  const canSubmitUpdate =
    hasMinimumUpdateFields &&
    !documentLinksAreDuplicate &&
    !isLoading &&
    (!pideUnidadesEmpaque || unidadesValidas)

  // Actualizar los valores cuando cambien las props
  useEffect(() => {
    setDocumento1(currentDocumento1 || '')
    setDocumento2(currentDocumento2 || '')
    setTransportadora(currentTransportadora || '')
    setGuia(currentGuia || '')
    setUnidadesEmpaque('')
  }, [currentDocumento1, currentDocumento2, currentTransportadora, currentGuia, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Verificar permisos antes de proceder
    if (!hasPermission('add_documents')) {
      toast.error('No tienes permisos para actualizar documentos')
      return
    }
    
    if (!hasMinimumUpdateFields) {
      toast.error('Debe ingresar al menos un campo para actualizar')
      return
    }

    if (documentLinksAreDuplicate) {
      toast.error(DUPLICATE_DOCUMENT_LINKS_MESSAGE)
      return
    }

    if (pideUnidadesEmpaque && !unidadesValidas) {
      toast.error('Ingrese las unidades de empaque')
      return
    }

    try {
      setIsLoading(true)
      const token = localStorage.getItem('accessToken')
      
      const updateData: any = {}
      
      if (documento1.trim()) {
        updateData.documento1 = documento1.trim()
      }
      
      if (documento2.trim()) {
        updateData.documento2 = documento2.trim()
      }

      if (pideUnidadesEmpaque) {
        updateData.extraDetails = {
          ...(currentExtraDetails || {}),
          unidades_empaque: Number(unidadesEmpaque)
        }
      }

      if (isHomecenter) {
        if (transportadora.trim()) {
          updateData.transportadora = transportadora.trim()
        }
        if (guia.trim()) {
          updateData.guia = guia.trim()
        }
        updateData.status = 'asignado'
      }

      // Si es Mercado Libre o Exito, actualizar también el estado a 'asignado'
      const tiendasConCambioEstado = ['Mercado Libre', 'Mercado libre', 'Exito'];
      if (tiendasConCambioEstado.includes(storeName)) {
        updateData.status = 'asignado'
      }

      await axiosInstance.patch(`/list/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      toast.success('Documentos actualizados correctamente')
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error actualizando documentos:', error)
      toast.error('Error al actualizar los documentos')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setDocumento1(currentDocumento1)
    setDocumento2(currentDocumento2)
    setTransportadora(currentTransportadora)
    setGuia(currentGuia)
    setUnidadesEmpaque('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Actualizar Documentos
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documento 1
            </label>
            <div className="relative">
              <input
                type="text"
                value={documento1}
                onChange={(e) => setDocumento1(e.target.value)}
                className={`w-full p-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                  documentLinksAreDuplicate
                    ? 'border-red-400 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Ingrese el documento 1"
                disabled={isLoading}
                aria-invalid={documentLinksAreDuplicate}
              />
              {documento1.trim() && (
                <button
                  type="button"
                  onClick={() => window.open(documento1.trim(), '_blank')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-700 transition-colors"
                  title="Abrir en nueva pestaña"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documento 2
            </label>
            <div className="relative">
              <input
                type="text"
                value={documento2}
                onChange={(e) => setDocumento2(e.target.value)}
                className={`w-full p-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                  documentLinksAreDuplicate
                    ? 'border-red-400 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Ingrese el documento 2"
                disabled={isLoading}
                aria-invalid={documentLinksAreDuplicate}
              />
              {documento2.trim() && (
                <button
                  type="button"
                  onClick={() => window.open(documento2.trim(), '_blank')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-700 transition-colors"
                  title="Abrir en nueva pestaña"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {pideUnidadesEmpaque && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidades de empaque
              </label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={unidadesEmpaque}
                onChange={(e) => {
                  const next = e.target.value
                  if (next === '' || /^\d+$/.test(next)) {
                    setUnidadesEmpaque(next)
                  }
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ingrese las unidades de empaque"
                aria-label="Unidades de empaque"
                disabled={isLoading}
                required
              />
            </div>
          )}

          {isHomecenter && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transportadora
                </label>
                <select
                  value={transportadora}
                  onChange={(e) => setTransportadora(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  <option value="">Seleccione una transportadora</option>
                  {transportadoraOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guía
                </label>
                <input
                  type="text"
                  value={guia}
                  onChange={(e) => setGuia(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ingrese la guía"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {documentLinksAreDuplicate && (
            <p
              className="text-sm text-red-600"
              role="alert"
              aria-live="polite"
            >
              {DUPLICATE_DOCUMENT_LINKS_MESSAGE}
            </p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            {hasPermission('add_documents') && (
              <button
                type="submit"
                disabled={!canSubmitUpdate}
                title={
                  documentLinksAreDuplicate
                    ? DUPLICATE_DOCUMENT_LINKS_MESSAGE
                    : undefined
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateDocumentsModal
